<template>
  <div class="flex flex-col items-start gap-2">
    <div class="relative inline-block" ref="menuWrapper" @mouseleave="handleMenuMouseLeave">
      <ButtonWhite
        @click="toggleMenu"
        activar="active"
        class="flex items-center gap-2"
        :disabled="isExporting"
      >
        {{ isExporting ? 'Exportando...' : 'Exportar' }}
        <ExportIcon
          class="w-4 inline-block"
          :class="{ 'rotate-180': showMenu }"
        />
        <div v-if="isExporting" class="ml-2">
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-[#385660]"></div>
        </div>
      </ButtonWhite>

      <transition name="fade">
        <div
          v-if="showMenu"
          ref="menuWrapper"
          @mouseleave="handleMenuMouseLeave"
          class="absolute right-0 mt-2 w-full text-center rounded-lg shadow-lg border-[#7CADB9] z-100 bg-white"
        >
          <ul>
            <li
              class="px-4 py-2 text-[#385660] hover:text-[#59E3FF] cursor-pointer gap-2"
              @click="exportFile('PDF')"
            >
              <span class="capitalize">PDF</span>
            </li>
            <li
              class="px-4 py-2 text-[#385660] hover:text-[#59E3FF] cursor-pointer gap-2 relative"
              @mouseenter="showExcelSubmenu = true"
              @mouseleave="showExcelSubmenu = false"
              @click.stop="showExcelSubmenu = !showExcelSubmenu"
            >
              <span class="capitalize">Excel</span>
              <div class="absolute left-full top-0" v-show="showExcelSubmenu" @mouseenter="showExcelSubmenu = true" @mouseleave="showExcelSubmenu = false" @click.stop>
                <div class="ml-2 bg-white rounded-lg shadow-lg border-[#7CADB9]">
                  <ul>
                    <li
                      v-for="format in excelFormats"
                      :key="format"
                      @click="exportFile(format); showMenu = false; showExcelSubmenu = false;"
                      class="px-4 py-2 text-[#385660] hover:text-[#59E3FF] cursor-pointer gap-2 whitespace-nowrap"
                    >
                      <span class="capitalize">{{ format }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </transition>
    </div>

    <!-- Filas marcadas en el listado, o todo lo filtrado (ADSREC-0029). La
         línea queda reservada para que la tabla no se mueva al marcar. -->
    <div v-if="selection" class="flex min-h-[1rem] flex-wrap items-center gap-2 text-xs text-[#385660]" role="status">
      <template v-if="selection.count > 0">
        <span>{{ selection.allFiltered ? $t('export.selection.all', selection.count) : $t('export.selection.rows', selection.count) }}</span>
        <button type="button" class="underline hover:text-[#2AB4D9] cursor-pointer" :disabled="isExporting" @click="selection.clear()">
          {{ $t('export.selection.clear') }}
        </button>
      </template>
    </div>

    <!-- Rango obligatorio, de un año como máximo: exportar todo colgaba el servidor (ADS-0186).
         Las filas marcadas a mano se exportan sin rango y en su lugar va un aviso (ADSREC-0029). -->
    <div v-if="dateRange" class="flex min-h-[46px] flex-wrap items-end gap-2">
      <p v-if="!showDateRange" class="max-w-xs self-center text-xs text-[#385660]">{{ $t('export.selection.noRange') }}</p>
      <template v-else>
        <label class="flex flex-col gap-1 text-xs text-[#385660]">
          <span>{{ fromLabel || $t('export.range.dateFrom') }}</span>
          <input v-model="rangeFrom" type="date" :max="rangeTo || undefined" :disabled="isExporting"
            class="rounded-md border border-gray-300 px-2 py-1 text-[#385660]">
        </label>
        <label class="flex flex-col gap-1 text-xs text-[#385660]">
          <span>{{ toLabel || $t('export.range.dateTo') }}</span>
          <input v-model="rangeTo" type="date" :min="rangeFrom || undefined" :disabled="isExporting"
            class="rounded-md border border-gray-300 px-2 py-1 text-[#385660]">
        </label>
      </template>
    </div>
    <p v-if="rangeMessage" class="max-w-xs text-xs text-red-600" role="alert">{{ rangeMessage }}</p>
  </div>
</template>

<script setup>
import axios from "axios";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { defaultExportRange, exportRangeError } from "@/services/exportDateRange";
import { EXPORT_SELECTION_MAX_IDS } from "@/composables/useRowSelection";
import ButtonWhite from "@/components/button/ButtonWhite.vue";
import ExportIcon from "@/assets/svg/ExportIcon.vue";
import { useExportStore } from '@/stores/exportStore'

const props = defineProps({
  id: {
    type: String,
    default: "",
  },
  exportEndpoint: {
    type: String,
    required: true,
  },
  fileName: {
    type: String,
    required: true,
  },
  exportType: {
    type: String,
    default: "",
  },
  property_type: {
    type: String,
    default: "",
  },
  // Filtros propios de cada exportación; el histórico general acota por fecha
  // de entrada igual que Exportar contratos (ADS-0082).
  extraParams: {
    type: Object,
    default: () => ({}),
  },
  // Pide fecha desde y hasta, de un año como máximo, y las manda como
  // date_from / date_to (ADS-0186). Las vistas lo apagan cuando la
  // exportación ya está acotada a una cuenta.
  dateRange: {
    type: Boolean,
    default: false,
  },
  fromLabel: {
    type: String,
    default: "",
  },
  toLabel: {
    type: String,
    default: "",
  },
  // Filas marcadas en el listado (ADSREC-0029): el useRowSelection de la
  // vista, envuelto en reactive(). Manda sus IDs y, si son filas marcadas a
  // mano, no pide rango de fechas.
  selection: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  "export-started",
  "export-completed",
  "export-failed",
]);
const showMenu = ref(false);
const isExporting = ref(false);
const showExcelSubmenu = ref(false);

const excelFormats = ["Excel Xls", "Excel Xlsx", "Excel Csv"];

const exportStore = useExportStore()
const { t } = useI18n();

const initialRange = defaultExportRange();
const rangeFrom = ref(initialRange.from);
const rangeTo = ref(initialRange.to);
const rangeMessage = ref("");
watch([rangeFrom, rangeTo, () => props.selection?.count], () => {
  rangeMessage.value = "";
});
const showDateRange = computed(() => props.dateRange && !props.selection?.picksRows);

const toggleMenu = () => {
  showMenu.value = !showMenu.value;
};

const exportFile = async (format) => {
  console.log("exportFile", format);
  rangeMessage.value = "";
  if (props.selection?.tooMany) {
    rangeMessage.value = t("export.selection.tooMany", { max: EXPORT_SELECTION_MAX_IDS });
    showMenu.value = false;
    showExcelSubmenu.value = false;
    return;
  }
  if (showDateRange.value) {
    const problem = exportRangeError(rangeFrom.value, rangeTo.value);
    if (problem) {
      rangeMessage.value = t(`export.range.${problem}`);
      showMenu.value = false;
      showExcelSubmenu.value = false;
      return;
    }
  }
  isExporting.value = true;
  exportStore.startExport(`Exportando a ${format}...`);
  // Define los tipos de archivo y extensiones según el formato elegido
  const formatMap = {
    "Excel Xlsx": {
      type: "xlsx",
      mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ext: "xlsx",
    },
    "Excel Xls": { type: "xls", mime: "application/vnd.ms-excel", ext: "xls" },
    "Excel Csv": { type: "csv", mime: "text/csv", ext: "csv" },
    PDF: { type: "pdf", mime: "application/pdf", ext: "pdf" },
  };

  const selected = formatMap[format];
  if (!selected) {
    console.log("Formato no soportado");
    return;
  }

  try {
    emit("export-started");
    exportStore.setStatus('processing', 'Iniciando exportación...');

    const exportResponse = await axios.get(
      `${import.meta.env.VITE_API_URL}${props.exportEndpoint}`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          file_name: props.fileName,
          file_type: selected.type,
          supply_type: props.exportType,
          // property_type: props.property_type,
          ...(props.id && { id: props.id }),
          ...Object.fromEntries(Object.entries(props.extraParams).filter(([, value]) => value)),
          ...(showDateRange.value ? { date_from: rangeFrom.value, date_to: rangeTo.value } : {}),
          ...(props.selection?.exportParams || {}),
        },
      }
    );
    const jobId = exportResponse.data.value.job_id;
    if (!jobId) {
      throw new Error("No se recibió job_id");
    }

    exportStore.setStatus('processing', 'Procesando archivo...');
    exportStore.updateProgress(30);

    const pollInterval = 2000;
    let polling;

    const checkStatus = async () => {
      try {
        const statusResponse = await axios.get(
          `${import.meta.env.VITE_API_URL}/export/status`,
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            params: {
              job_id: jobId,
            },
          }
        );
        console.log(statusResponse.data.value.status);

        if (statusResponse.data.value.status == "finished") {
          clearInterval(polling);
          emit("export-completed");
          isExporting.value = false;
          exportStore.updateProgress(90);
          exportStore.setStatus('processing', 'Descargando archivo...');

          const filePath = statusResponse.data.value.file_path;
          if (filePath) {
            try {
              const downloadResponse = await axios.get(
                `${import.meta.env.VITE_API_URL}/export/download`,
                {
                  headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                  },
                  params: {
                    path: filePath,
                  },
                  responseType: "blob",
                }
              );

              const blob = new Blob([downloadResponse.data], {
                type: selected.mime,
              });
              const link = document.createElement("a");
              link.href = window.URL.createObjectURL(blob);
              link.download = `${props.fileName}.${selected.ext}`;
              link.click();
              exportStore.completeExport();
            } catch (error) {
              console.error("Error al descargar el archivo:", error);
              emit("export-failed");
              exportStore.failExport('Error al descargar el archivo');
            }
          } else {
            console.log("No se pudo obtener la ruta del archivo.");
            emit("export-failed");
            exportStore.failExport('No se pudo obtener la ruta del archivo');
          }
        } else if (statusResponse.data.value.status === "failed") {
          clearInterval(polling);
          emit("export-failed");
          isExporting.value = false;
          exportStore.failExport('La exportación falló');
          console.log("¡La exportación falló!");
        }
      } catch (err) {
        clearInterval(polling);
        emit("export-failed");
        isExporting.value = false;
        exportStore.failExport('Error verificando estado');
        console.error("Error verificando estado:", err);
      }
    };

    polling = setInterval(checkStatus, pollInterval);
    checkStatus();
    showMenu.value = false;
  } catch (error) {
    isExporting.value = false;
    // La API rechaza un rango que falta o pasa de un año: se muestra su motivo.
    if (error.response?.status === 422) {
      rangeMessage.value = Object.values(error.response.data?.errors || {})[0]?.[0] || t("export.range.required");
    }
    emit("export-failed");
    exportStore.failExport('Error en la exportación');
    console.error("Error exporting:", error);
  }
};

// Cierra el menú principal solo si el mouse sale de ambos menús
const menuWrapper = ref(null);

const handleMenuMouseLeave = (e) => {
  // Si el mouse sale completamente del menú y submenú, cerrar
  if (!menuWrapper.value.contains(e.relatedTarget)) {
    showMenu.value = false;
    showExcelSubmenu.value = false;
  }
};

defineExpose({
  exportFile,
  toggleMenu,
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
